/**
 * The two task-state signals that exist only for the desk.
 *
 * The desk (`components/task-story/Desk.tsx`) says what Bash is doing, and
 * every word it can say is derivable from the story rows and the task's phase
 * except two:
 *
 *   · "listening" — the user is drafting a reply. Only the composer knows
 *     that, and nothing in the agent's stream can imply it.
 *   · "trying again" — a miss just landed. The error is already RENDERED by
 *     the streaming handlers (a card, or deliberately nothing), but none of
 *     them records WHEN, and the timestamp is the whole question: is a turn
 *     that is still running working through something that just went wrong?
 *
 * Their own module rather than lines inside `taskStore.ts` and
 * `handlers/streamingHandlers.ts`, both of which are size-ratcheted and only
 * shrink (apps/reconciler/data/ratchets/file-size-baseline.json). It imports
 * the store rather than taking the `{ get, set }` seam, which is the direction
 * that stays acyclic here: nothing in `taskStore.ts` imports this file — the
 * composer calls the setter directly and `socketHandlers` wires the registrar.
 */
import type { Socket } from 'socket.io-client';
import { EVENTS } from '@/constants/events';
import { useTaskStore } from './taskStore';

/** Words in the box, with the box in hand. Called from the composer's change
 *  handler, so on EVERY keystroke: it writes only on a real flip, or each
 *  character pays for an Immer draft, a persist schedule, and a re-render of
 *  every subscriber to this task. No-ops on a task that is not in the map. */
export function setComposerTyping(taskId: string, typing: boolean): void {
  const store = useTaskStore.getState();
  if (store.tasks.get(taskId)?.composerTyping === typing) return;
  store._mutateTasks(tasks => {
    const task = tasks.get(taskId);
    if (task) task.composerTyping = typing;
  });
}

/** The shape of AGENT_ERROR this cares about: which task, and nothing else. */
type AgentErrorEvent = { sessionId?: string; taskId?: string };

/**
 * Stamps the miss. A listener of its own beside the streaming handler's, which
 * Socket.io fans the same event out to: this reads nothing that one writes and
 * writes nothing it reads, so it cannot perturb the rendering decision and it
 * stamps whichever branch over there ends up being taken.
 *
 * That last part is the point. The LOUDEST beat for the desk is the recoverable
 * class (`rate_limited` / `network_error`), where the handler deliberately
 * commits no card at all because the agent works through it — exactly when the
 * face is the only surface that can tell the user anything. A stamp placed
 * inside that handler, after its early returns, would have been dead for its
 * main use.
 */
export function registerLastErrorStamp(socket: Socket): () => void {
  const stamp = (data: AgentErrorEvent) => {
    // Same identity fallback the streaming handler uses — a legacy emitter
    // still addresses the task by its session id.
    const taskId = data.taskId || data.sessionId;
    if (!taskId) return;
    useTaskStore.getState()._mutateTasks(tasks => {
      const task = tasks.get(taskId);
      if (task) task.lastErrorAt = Date.now();
    });
  };

  socket.on(EVENTS.AGENT_ERROR, stamp);
  return () => { socket.off(EVENTS.AGENT_ERROR, stamp); };
}
